import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { useEffect, useState, useMemo, memo, useCallback, forwardRef, useRef, type ElementRef, type ComponentPropsWithoutRef } from "react";
import { AssetClassContent } from "./components/AssetClassContent";
import { AssetClassHeader } from "./components/AssetClassHeader";
import { useAllocationManagement } from "./hooks/useAllocationManagement";
import { ChevronDown } from "lucide-react";
import { formatCurrency } from "@/lib/portfolioCalculations";
import { ASSET_CLASS_COLORS } from "@/constants/investmentColors";
import { ASSET_CLASS_ICONS } from "@/constants/investmentIcons";
import { cn } from "@/lib/utils";

interface AssetClass {
  id: string;
  title: string;
}

interface Allocation {
  id: string;
  asset_id: string;
  asset_type?: string;
  fund?: {
    id?: string;
    fund_name?: string;
    investment_strategy?: string;
    asset_class?: string; // This might be missing in some cases
  };
  allocation_percentage: number;
}

interface AssetClassAccordionProps {
  assetClasses: AssetClass[];
  getAllocationsForAssetClass: (assetClass: string) => Allocation[];
  portfolioId: string;
  onAssetAdded: () => void;
  portfolioSize: number;
  isPortfolioSizeLocked: boolean;
  onPortfolioSizeChange?: (newSize: number) => Promise<boolean>;
  selectedIds?: Set<string>;
  toggleItem?: (id: string) => void;
  toggleAll?: (ids: string[]) => void;
  // Editing mode based on portfolio type: model => 'percentage', client/personal => 'dollar'
  mode?: 'percentage' | 'dollar';
  // Optimistic overrides lifted to parent
  overrides?: Record<string, number>;
  onLocalPercentageChange?: (allocationId: string, newPct: number) => void;
  onLocalDollarChange?: (allocationId: string, newDollars: number) => void;
  // Display/base currency code for formatting and input symbols
  currencyCode?: string;
}

const getFundTypeForAssetClass = (assetClassId: string): string => {
  switch (assetClassId) {
    case 'equity': return 'stocks';  // Map modern equity to stocks for dialog
    case 'stocks': return 'stocks'; // Keep legacy mapping
    case 'fixed_income': return 'bonds'; // Map modern fixed_income to bonds
    case 'bonds': return 'bonds'; // Keep legacy mapping
    case 'cash': return 'cash';
    case 'alternative': return 'alternative';
    case 'multi_asset': return 'all'; // Map multi_asset correctly
    default: return 'all';
  }
};

const SimpleAccordionTrigger = forwardRef<
  ElementRef<typeof AccordionPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn("flex w-full items-center py-4 text-left", className)}
      {...props}
    >
      {children}
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));

SimpleAccordionTrigger.displayName = "SimpleAccordionTrigger";

const AssetClassItem = memo(({ 
  assetClass, 
  allocations, 
  portfolioId, 
  portfolioSize,
  isPortfolioSizeLocked,
  onAssetAdded,
  onDelete,
  onUpdatePercentage,
  onUpdateDollarAmount,
  assetClassTotal,
  assetClassDollarTotal,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  mode,
  onLocalPercentageChange,
  onLocalDollarChange,
  overrides,
  currencyCode,
}: any) => {
  const classIds = useMemo(() => (allocations?.map((a: any) => a.id) ?? []), [allocations]);
  const selectedSet: Set<string> = selectedIds ?? new Set<string>();
  const selectedCount = classIds.filter((id: string) => selectedSet.has(id)).length;
  const allSelected = classIds.length > 0 && selectedCount === classIds.length;
  const someSelected = selectedCount > 0 && selectedCount < classIds.length;

  return (
    <AccordionItem value={assetClass.id} className="border rounded-lg relative overflow-hidden">
      {/* Colored left-side indicator - grey when no allocations exist, asset color when allocated */}
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{
        backgroundColor: (allocations?.length || 0) > 0 
          ? ASSET_CLASS_COLORS[assetClass.id as keyof typeof ASSET_CLASS_COLORS] || '#848482'
          : '#9ca3af' // Grey for unallocated asset classes
      }}></div>
      <AccordionPrimitive.Header className="w-full">
        <div className="grid grid-cols-[auto_auto_minmax(0,1fr)_240px_auto] items-center gap-3 px-4 py-2 w-full min-h-[44px]">
          {onToggleSelectAll ? (
            <div>
              <Checkbox
                checked={allSelected ? true : (someSelected ? ("indeterminate" as const) : false)}
                onCheckedChange={() => onToggleSelectAll?.(classIds)}
                aria-label="Select all in asset class"
              />
            </div>
          ) : (
            <span aria-hidden="true" />
          )}
          <AccordionPrimitive.Trigger asChild>
            <div
              className={cn(
                "group col-start-2 col-span-4 hover:no-underline flex w-full items-center py-0 text-left"
              )}
            >
              {(() => {
                const IconComponent = ASSET_CLASS_ICONS[assetClass.id as keyof typeof ASSET_CLASS_ICONS] || ASSET_CLASS_ICONS.cash;
                return <IconComponent className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />;
              })()}
              <div className="text-left min-w-0 ml-3">
                <AssetClassHeader 
                  title={assetClass.title}
                />
              </div>
              <div className="ml-auto w-[240px] justify-self-end text-right whitespace-nowrap tabular-nums text-xs text-muted-foreground flex items-center justify-end gap-2">
                <span>{formatCurrency(assetClassDollarTotal, currencyCode)}</span>
                <span>•</span>
                <span>{assetClassTotal.toFixed(2)}%</span>
              </div>
              <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180 ml-3" />
            </div>
          </AccordionPrimitive.Trigger>
        </div>
      </AccordionPrimitive.Header>
      <AccordionContent className="px-4 pt-2 pb-4">
        <AssetClassContent
          allocations={allocations}
          assetClassTotal={assetClassTotal}
          assetClassDollarTotal={assetClassDollarTotal}
          portfolioId={portfolioId}
          portfolioSize={portfolioSize}
          isPortfolioSizeLocked={isPortfolioSizeLocked}
          onAssetAdded={onAssetAdded}
          onDelete={onDelete}
          onUpdatePercentage={onUpdatePercentage}
          onUpdateDollarAmount={onUpdateDollarAmount}
          initialFundType={getFundTypeForAssetClass(assetClass.id)}
          selectedIds={selectedIds}
          onToggleSelect={onToggleSelect}
          onSuccess={onAssetAdded}
          mode={mode}
          onLocalPercentageChange={onLocalPercentageChange}
          onLocalDollarChange={onLocalDollarChange}
          overrides={overrides}
          currencyCode={currencyCode}
        />
      </AccordionContent>
    </AccordionItem>
  );
});

AssetClassItem.displayName = 'AssetClassItem';

export const AssetClassAccordion = memo(({ 
  assetClasses,
  getAllocationsForAssetClass,
  portfolioId,
  onAssetAdded,
  portfolioSize,
  isPortfolioSizeLocked,
  onPortfolioSizeChange,
  selectedIds,
  toggleItem,
  toggleAll,
  mode,
  overrides = {},
  onLocalPercentageChange,
  onLocalDollarChange,
  // Display/base currency code for formatting and input symbols
  currencyCode,
}: AssetClassAccordionProps) => {
  const effectiveMode: 'percentage' | 'dollar' = mode ?? 'percentage';
  const [openItems, setOpenItems] = useState<string[]>(() => {
    try {
      if (typeof window === 'undefined') return [];
      const saved = window.localStorage.getItem(`accordion-state-${portfolioId}`);
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const { handleDeleteAllocation, handleUpdateAllocation, handleUpdateAllocationByValue } = useAllocationManagement(onAssetAdded, currencyCode, portfolioId);

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(`accordion-state-${portfolioId}`, JSON.stringify(openItems));
    } catch {
      // ignore write errors
    }
  }, [openItems, portfolioId]);

  const handleAccordionChange = useCallback((value: string[]) => {
    setOpenItems(value);
  }, []);

  const handleDollarAmountChange = useCallback((allocation: Allocation, dollarAmount: number) => {
    if (portfolioSize <= 0) return;
    
    // Debug logging to track lock state propagation
    console.debug('[AssetClassAccordion] Dollar amount change:', {
      allocationId: allocation.id,
      dollarAmount,
      portfolioSize,
      isPortfolioSizeLocked,
      hasOnPortfolioSizeChange: !!onPortfolioSizeChange
    });
    
    handleUpdateAllocationByValue(allocation.id, dollarAmount, portfolioSize, isPortfolioSizeLocked, onPortfolioSizeChange);
  }, [portfolioSize, isPortfolioSizeLocked, handleUpdateAllocationByValue, onPortfolioSizeChange]);

  const assetClassItems = useMemo(() => 
    assetClasses.map((assetClass) => {
      const allocations = getAllocationsForAssetClass(assetClass.id);
      const assetClassTotal = allocations.reduce((sum, allocation) => {
        const effectivePct = (overrides[allocation.id] ?? allocation.allocation_percentage) || 0;
        return sum + effectivePct;
      }, 0);
      const assetClassDollarTotal = (assetClassTotal / 100) * portfolioSize;

      return {
        assetClass,
        allocations,
        assetClassTotal,
        assetClassDollarTotal
      };
    }), [assetClasses, getAllocationsForAssetClass, overrides, portfolioSize]);

  // On a brand-new portfolio (no allocations yet), default-open the Equity section once to guide new users
  const didInitDefaultOpen = useRef(false);
  useEffect(() => {
    if (didInitDefaultOpen.current) return;
    // Only if nothing is currently open and there are no allocations at all
    const hasAnyAllocations = assetClassItems.some((i) => (i.allocations?.length || 0) > 0);
    if (!hasAnyAllocations && openItems.length === 0) {
      // Ensure 'equity' exists in provided assetClasses
      const hasEquity = assetClasses.some((a) => a.id === 'equity');
      if (hasEquity) {
        setOpenItems(['equity']);
        didInitDefaultOpen.current = true;
      }
    }
  }, [assetClassItems, openItems.length, assetClasses]);

  return (
    <Accordion 
      type="multiple" 
      className="w-full space-y-4"
      value={openItems}
      onValueChange={handleAccordionChange}
    >
      {assetClassItems.map(({ assetClass, allocations, assetClassTotal, assetClassDollarTotal }) => (
        <AssetClassItem
          key={assetClass.id}
          assetClass={assetClass}
          allocations={allocations}
          portfolioId={portfolioId}
          portfolioSize={portfolioSize}
          isPortfolioSizeLocked={isPortfolioSizeLocked}
          onAssetAdded={onAssetAdded}
          onDelete={handleDeleteAllocation}
          onUpdatePercentage={handleUpdateAllocation}
          onUpdateDollarAmount={handleDollarAmountChange}
          assetClassTotal={assetClassTotal}
          assetClassDollarTotal={assetClassDollarTotal}
          selectedIds={selectedIds}
          onToggleSelect={toggleItem}
          onToggleSelectAll={toggleAll}
          mode={effectiveMode}
          onLocalPercentageChange={onLocalPercentageChange}
          onLocalDollarChange={onLocalDollarChange}
          overrides={overrides}
          currencyCode={currencyCode}
        />
      ))}
    </Accordion>
  );
});

AssetClassAccordion.displayName = 'AssetClassAccordion';
